import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import {
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { ContentBoxComponent } from '@app/shared/content-box/content-box.component';
import { CustomPaginatorIntl } from '@app/shared/custom-paginator/custom-paginator.component';
import { Labels } from '@core/constants/labels';
import { Role } from '@core/models/role.model';
import { User } from '@core/models/user.model';
import { UserService } from '@core/services/user-service/user-service.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { EditUserDialogComponent } from './edit-user-dialog/edit-user-dialog.component';
import { InviteUserDialogComponent } from './invite-user-dialog/invite-user-dialog.component';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule,
    ContentBoxComponent,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatDialogModule,
  ],
  providers: [
    UserService,
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },
  ],
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss',
})
export class UserManagementComponent implements OnInit, OnDestroy {
  users: User[] = [];
  subscriptions = new Subscription();
  labels = Labels.USER_MANAGEMENT;
  roleModel = Role;
  count = 0;
  pageSize = 10;
  pageIndex = 0;
  hidePageSize = true;
  showFirstLastButtons = true;
  active_users_view = true;
  isDesktop = false;
  isMobile = false;
  displayedColumns: string[] = [];
  readonly dialog = inject(MatDialog);

  ngOnInit() {
    this.setDisplayedColumns();
    this.loadUsers();
    // Subscribe to window resize events
    const resizeObservable = fromEvent(window, 'resize');
    const resizeSubscription = resizeObservable
      .pipe(
        debounceTime(300), // Debounce to avoid excessive calls
      )
      .subscribe(() => {
        this.setDisplayedColumns();
        this.loadUsers();
      });
    this.subscriptions.add(resizeSubscription);
  }

  setDisplayedColumns() {
    this.isDesktop = window.innerWidth > 1245;
    this.isMobile = window.innerWidth < 950;
    if (this.isDesktop) {
      this.displayedColumns = [
        'full_name',
        'email',
        'crm',
        'crm_state',
        'role',
        'tenant',
        'actions',
      ];
    } else if (this.isMobile) {
      this.displayedColumns = ['full_name', 'email', 'role'];
    } else {
      this.displayedColumns = ['full_name', 'email', 'role', 'actions'];
    }
  }
  dataSource: MatTableDataSource<User>;

  constructor(
    private userService: UserService,
    private router: Router,
    private toastrService: ToastrService,
  ) {
    this.dataSource = new MatTableDataSource<User>([]);
  }

  handlePageEvent(e: PageEvent) {
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    const currentFilterData = this.userService.filterData;
    currentFilterData.page_size = this.pageSize;
    currentFilterData.page = this.pageIndex;
    this.userService.updateFilters(currentFilterData);
    this.loadUsers();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  loadUsers() {
    // Calculate the pageSize based on the display height
    const displayHeight = window.innerHeight;
    const currentFilterData = this.userService.filterData;
    currentFilterData.page_size = Math.floor(
      (16 * (displayHeight - 200)) / 1174,
    );
    // Ensure the pageSize is at least 1
    currentFilterData.page_size = Math.max(1, currentFilterData.page_size);
    this.userService.updateFilters(currentFilterData);
    const sub = this.userService
      .getUsers(this.active_users_view)
      .subscribe((data: any) => {
        this.users = data.results as User[];
        this.dataSource.data = this.users;
        this.pageSize = this.userService.filterData.page_size;
        this.pageIndex = this.userService.filterData.page;
        this.count = data.count;
      });
    this.subscriptions.add(sub);
  }

  openInviteUser() {
    const dialogRef = this.dialog.open(InviteUserDialogComponent);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastrService.success(this.labels.USER_INVITED);
        this.loadUsers();
      }
    });
  }

  openEditUserDialog(user: User) {
    const dialogRef = this.dialog.open(EditUserDialogComponent, {
      data: user,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const updatedUser = result as User;
        const index = this.users.findIndex((u) => u.id === updatedUser.id);
        if (index !== -1) {
          this.users[index] = updatedUser;
          this.dataSource.data = [...this.users];
          this.toastrService.success(this.labels.USER_UPDATED);
        }
      }
    });
  }

  toggleActivateUser(user: User) {
    this.userService
      .updateUser(user, { is_active: !user.is_active })
      .subscribe(() => {
        this.toastrService.success(this.labels.USER_UPDATED);
        this.loadUsers();
      });
  }

  toggleActiveUsersView() {
    this.active_users_view = !this.active_users_view;
    this.pageIndex = 0;
    this.userService.filterData.page = 0;
    this.loadUsers();
  }

  sortData(sort: Sort) {
    const data = this.users.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'full_name':
          return compare(a.full_name, b.full_name, isAsc);
        case 'email':
          return compare(a.email, b.email, isAsc);
        case 'role':
          return compare(
            this.roleModel.fromNumber(a.role),
            this.roleModel.fromNumber(b.role),
            isAsc,
          );
        default:
          return 0;
      }
    });
  }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
